// src/components/admin/DialogCambiarPassword.js
//
// Cambio de contraseña desde el panel del SUPERADMIN, para cualquier usuario
// de la plataforma: tendero, veci, domiciliario, guía, validador u otro
// administrador. Es soporte telefónico: alguien no puede entrar y no le
// llega el correo de recuperación.
//
// Todos los listados lo usan con el mismo contrato: recibe el AspNetUserId y
// un nombre para mostrar. Si la entidad todavía no tiene usuario vinculado
// (un domiciliario pre-registrado por el negocio, por ejemplo), el listado no
// debe ofrecer la acción: acá se avisa y no se deja continuar.

import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  Stack, Alert, InputAdornment, IconButton, Typography, CircularProgress,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { admin } from '../../api/authApi';

const MINIMO = 6;

/** Contraseña temporal legible por teléfono: sin caracteres que se confundan. */
function generarPassword() {
  const letras = 'abcdefghijkmnpqrstuvwxyz';
  const mayus  = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const nums   = '23456789';
  const al = (s) => s[Math.floor(Math.random() * s.length)];
  return (
    al(mayus) + al(letras) + al(letras) + al(letras) +
    al(nums) + al(nums) + al(letras) + al(nums)
  );
}

/**
 * @param {object}   props
 * @param {boolean}  props.abierto
 * @param {string}   props.aspNetUserId  usuario destino (null => no tiene cuenta)
 * @param {string}   props.nombre        para mostrar de quién es la cuenta
 * @param {string}   [props.detalle]     correo o teléfono, como referencia
 * @param {Function} props.onCerrar
 * @param {Function} [props.onCambiada]  (mensaje) => void
 */
export default function DialogCambiarPassword({
  abierto, aspNetUserId, nombre, detalle, onCerrar, onCambiada,
}) {
  const [password, setPassword]   = useState('');
  const [repetir, setRepetir]     = useState('');
  const [ver, setVer]             = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError]         = useState('');
  const [copiada, setCopiada]     = useState(false);

  useEffect(() => {
    if (abierto) {
      setPassword(''); setRepetir(''); setVer(false);
      setError(''); setCopiada(false);
    }
  }, [abierto]);

  const corta      = password.length > 0 && password.length < MINIMO;
  const noCoincide = repetir.length > 0 && password !== repetir;
  const puede      = !!aspNetUserId && password.length >= MINIMO && password === repetir && !guardando;

  const sugerir = () => {
    const nueva = generarPassword();
    setPassword(nueva); setRepetir(nueva); setVer(true); setCopiada(false);
  };

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(password);
      setCopiada(true);
    } catch {
      setError('No se pudo copiar: selecciónala y cópiala a mano.');
    }
  };

  const guardar = async () => {
    setError(''); setGuardando(true);
    try {
      const r = await admin.usuarioCambiarPassword(aspNetUserId, password);
      onCambiada?.(r?.advertencia
        ? `Contraseña actualizada. ${r.advertencia}`
        : `Contraseña de ${nombre} actualizada.`);
      onCerrar();
    } catch (e) {
      setError(e.mensajeUsuario || 'No se pudo cambiar la contraseña.');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog open={!!abierto} onClose={guardando ? undefined : onCerrar} maxWidth="xs" fullWidth>
      <DialogTitle>Cambiar contraseña</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <div>
            <Typography fontWeight={700}>{nombre || 'Usuario'}</Typography>
            {detalle && (
              <Typography variant="body2" color="text.secondary">{detalle}</Typography>
            )}
          </div>

          {!aspNetUserId && (
            <Alert severity="warning">
              Esta persona todavía no tiene usuario de acceso vinculado, así que no hay
              contraseña que cambiar.
            </Alert>
          )}

          {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}

          <TextField
            label="Contraseña nueva" fullWidth autoFocus
            type={ver ? 'text' : 'password'}
            value={password} disabled={!aspNetUserId || guardando}
            onChange={(e) => { setPassword(e.target.value); setCopiada(false); }}
            error={corta}
            helperText={corta ? `Mínimo ${MINIMO} caracteres.` : ' '}
            autoComplete="new-password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setVer(v => !v)} edge="end">
                    {ver ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            label="Repite la contraseña" fullWidth
            type={ver ? 'text' : 'password'}
            value={repetir} disabled={!aspNetUserId || guardando}
            onChange={(e) => setRepetir(e.target.value)}
            error={noCoincide}
            helperText={noCoincide ? 'No coinciden.' : ' '}
            autoComplete="new-password"
            onKeyDown={(e) => { if (e.key === 'Enter' && puede) guardar(); }}
          />

          <Stack direction="row" spacing={1}>
            <Button size="small" startIcon={<AutorenewIcon />} onClick={sugerir} disabled={!aspNetUserId || guardando}>
              Sugerir una
            </Button>
            <Button
              size="small" startIcon={<ContentCopyIcon />} onClick={copiar}
              disabled={!password || guardando}
            >
              {copiada ? 'Copiada' : 'Copiar'}
            </Button>
          </Stack>

          <Alert severity="info">
            Al guardar, el usuario entra con esta contraseña y se cierran sus sesiones
            abiertas. Dísela por un medio seguro y pídele que la cambie desde su perfil.
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCerrar} disabled={guardando}>Cancelar</Button>
        <Button
          variant="contained" onClick={guardar} disabled={!puede}
          startIcon={guardando ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {guardando ? 'Guardando…' : 'Cambiar contraseña'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
